import {
  NodeId,
  NodeModel,
  Orientation,
  Side,
  SwimlaneGroupNodeModel,
  SwimlaneNodeModel,
} from '@diagram/model/node.model';
import { GetState, SetState } from '../store.types';
import { compileResizeChanges } from '../store.utils';
import { applyResizeAndMakePatches } from './resize.utils';
import { ChangeModel } from '@diagram/model/change.model';
import { Rect } from '@xyflow/react';

type ResizeSession = {
  activeId: NodeId | null;
  orientation: Orientation | null;
  groupNode: NodeModel | null;
  laneNodesById: Record<string, NodeModel> | null;
  shiftKey: boolean;
  start: Rect | null;
  lastChanges: ChangeModel[] | [];
  side: Side | null;
};

type ReadyResizeSession = Omit<
  ResizeSession,
  'activeId' | 'dir' | 'orientation' | 'groupNode' | 'laneNodesById' | 'start'
> & {
  activeId: NodeId;
  orientation: Orientation;
  groupNode: SwimlaneGroupNodeModel;
  laneNodesById: Record<string, SwimlaneNodeModel>;
  start: Rect;
};

const isResizeSessionReady = (
  session: ResizeSession
): session is ReadyResizeSession => {
  return (
    session.activeId !== null &&
    session.orientation !== null &&
    session.groupNode !== null &&
    session.laneNodesById !== null &&
    session.start !== null
  );
};

export interface ResizeSlice {
  resizeSession: ResizeSession;
  beginResize(id: NodeId, shiftKey: boolean, side: Side, start: Rect): void;
  resizeTo(dx: number, dy: number, gutter?: number): void;
  endResize(): void;
}

export const createResizeSlice = (set: SetState, get: GetState) =>
  ({
    resizeSession: {
      activeId: null,
      orientation: null,
      groupNode: null,
      laneNodesById: null,
      shiftKey: false,
      start: null,
      lastChanges: [],
      side: null,
    },
    beginResize: (id, shiftKey, side, start) => {
      const activeNode = get().nodesById[id];
      if (!activeNode) return;

      // let ReactFlow handle non-swimlane nodes resizing
      if (activeNode.type !== 'swimlane') return;

      const { sortedLanes, group } =
        get().swimlane.getFullSwimlaneGroup(id) || {};
      if (!group || !sortedLanes || sortedLanes.length === 0) return;
      const changes = compileResizeChanges([group, ...sortedLanes], 'start');

      const laneNodesById = sortedLanes.reduce((acc, lane) => {
        acc[lane.id] = lane;
        return acc;
      }, {} as Record<string, NodeModel>);

      // record starting position as start of operation
      get().startOperation(changes);

      set((s) => {
        s.resizeSession = {
          activeId: id,
          orientation: group.data.orientation as Orientation,
          groupNode: group,
          laneNodesById,
          shiftKey,
          start,
          side,
          lastChanges: [],
        };
        return s;
      }, false);
    },

    resizeTo: (dx, dy, gutter = 0) => {
      const session = get().resizeSession;
      if (!isResizeSessionReady(session)) return;
      const { orientation, shiftKey, activeId } = session;

      const { group, sortedLanes } =
        get().swimlane.getFullSwimlaneGroup(activeId) || {};

      if (!group || !sortedLanes || sortedLanes.length === 0) return;

      const { patches, changes } = applyResizeAndMakePatches({
        orientation,
        dx,
        dy,
        side: session.side as Side,
        shiftKey,
        groupNode: group,
        laneNodes: sortedLanes,
        activeLaneId: activeId,
        gutter,
      });

      get().patchResizeNodes(patches);
      set((s) => {
        s.resizeSession.lastChanges = changes;
        return s;
      });
      get().addChangesToCurrentOperation(changes);
    },
    endResize() {
      const changes = get().resizeSession.lastChanges;
      const activeId = get().resizeSession.activeId;
      const finalizeChanges = changes.map(
        (change) =>
          ({
            ...change,
            status: 'end',
          } as ChangeModel)
      );
      get().selectNode(activeId as NodeId);
      get().finalizeOperation(finalizeChanges);
      set((s) => {
        s.resizeSession = {
          activeId: null,
          orientation: null,
          groupNode: null,
          laneNodesById: null,
          shiftKey: false,
          start: null,
          lastChanges: [],
          side: null,
        };
        return s;
      });
    },
  } satisfies ResizeSlice);
